import React from "react"
import { useFormikContext } from "formik"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"

import VoiceInputButton from "@components/ui/Button/VoiceInputButton"
import { useController, useStore } from "@store/index"
import { useSpeechRecognizer } from "@pages/search/components/SearchWidget/useSpeechRecognizer"
import SearchInputField from "@pages/search/components/SearchWidget/SearchInputField"
import useMediaQuery from "@components/hooks/useMediaQuery"
import { AppMediaQueries } from "@framework/constants/app"
import QUERY_ATTACHMENT_EXTENSIONS from "@framework/constants/query-attachments"
import Icon from "@components/ui/Icon/Icon"
import Button from "@components/ui/Button/Button"
import Chip from "@components/ui/Chip/Chip"
import AttachDocumentButton from "@components/ui/DocumentDropZone/AttachDocumentButton"
import QueryAttachment from "@store/search/query-attachment"
import { SEARCH_MODE_OPTIONS } from "@framework/types/search-mode"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"

import { SearchSubmitButton } from "./SearchSubmitButton"
import { useSearchWidget } from "./components/Tabs/FactFinder/components/SearchWidget/SearchWidgetContext"

import styles from "./SearchInput.module.sass"

interface Props {
  disabled?: boolean
  loading?: boolean
}

export const SearchInput: React.FC<Props> = observer(
  ({ disabled, loading }) => {
    const alert = useAlert()
    const {
      restrictionsStore: access,
      factFinderSolutionStore: { searchFlowStore, queryAttachments },
    } = useStore()
    const { factFinderSolutionController: controller } = useController()
    const isMobile = useMediaQuery(AppMediaQueries.maxMobile)
    const formik = useFormikContext<any>()

    const searchWidget = useSearchWidget()

    const speechControl = useSpeechRecognizer({
      onChange: (value) => {
        formik.setFieldValue("query", value)
      },
    })

    const handleAttach = async (files: File[]) => {
      if (!files.length) {
        return
      }

      if (queryAttachments.hasAttachments) {
        alert.error("Remove the current attachment before adding another file.")
        return
      }

      if (loading) {
        alert.error("Please wait for the current search to finish before attaching a file.")
        return
      }

      const attachment = new QueryAttachment(files[0])

      try {
        await controller.createAttachment(attachment)
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Could not add attachment, please try again later."
        alert.error(String(message))
      }
    }

    const isListening = speechControl.listening
    const canSearch =
      !disabled && !isListening && formik.values.query.length > 0
    const isAttachmentLoading = queryAttachments.attachments.some(
      (item) => item.isLoading
    )

    const hasSettings = true
    const activeMode = SEARCH_MODE_OPTIONS.find(
      (option) => option.value === searchFlowStore.searchMode
    )

    return (
      <>
        {!searchWidget.isSettingsShown && activeMode && (
          <Chip
            variant="rounded"
            color="secondary-solid"
            className={styles.activeModeChip}
            title={activeMode.description}
          >
            {activeMode.label}
          </Chip>
        )}

        <SearchInputField
          disabled={isListening || disabled}
          placeholder="Ask a question"
          name="query"
          onFocus={() => searchWidget.setActive(true)}
          autoFocus
        />

        {access.isSearchVoiceRecognitionEnabled && (
          <VoiceInputButton
            disabled={disabled}
            size={isMobile ? "small" : "big"}
            listening={isListening}
            error={speechControl.error}
            onClick={() => speechControl.toggle()}
          />
        )}

        <Tooltip color="primary" closeOnTargetPress>
          <TooltipTrigger>
            <AttachDocumentButton
              iconOnly
              size={isMobile ? "small" : "big"}
              variant="outlined"
              noPadding
              multiple={false}
              extensions={QUERY_ATTACHMENT_EXTENSIONS}
              disabled={disabled || loading || queryAttachments.hasAttachments}
              loading={isAttachmentLoading}
              onAttach={handleAttach}
              title="Attach file"
            />
          </TooltipTrigger>
          <TooltipContent>
            Attach a PDF, Word, PowerPoint, or image file
          </TooltipContent>
        </Tooltip>

        {hasSettings && (
          <Tooltip color="primary" closeOnTargetPress>
            <TooltipTrigger>
              <Button
                size={isMobile ? "small" : "big"}
                variant="outlined"
                noPadding
                onClick={() =>
                  searchWidget.showSettings(!searchWidget.isSettingsShown)
                }
              >
                <Icon
                  name={searchWidget.isSettingsShown ? "arrow-down" : "dots"}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {searchWidget.isSettingsShown ? "Hide" : "Show"} Tools
            </TooltipContent>
          </Tooltip>
        )}

        <SearchSubmitButton
          loading={loading}
          disabled={!canSearch || loading}
          className={styles.searchButton}
          onStop={() => controller.cancelSearch()}
        />
      </>
    )
  }
)

export default SearchInput
